// Marketing trackers (reb2b visitor identification + LinkedIn Insight Tag) —
// loaded only on the standalone marketing/legal surface and only after the
// visitor has accepted marketing cookies. Route gating stays in React so a
// "Reject" choice keeps the trackers off the network.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useConsent } from "./consent";
import { isPublicPath } from "./publicPaths";


const REB2B_KEY = "QO92DHLMQ0N7";
const LINKEDIN_PARTNER_ID = "9372620";


// Tag a fresh <script> so the failing-host fallback (no existing
// <script> elements) appends into <head> rather than throwing.
function appendScript(src: string): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) {
    first.parentNode.insertBefore(s, first);
  } else {
    (document.head || document.documentElement).appendChild(s);
  }
}


function injectReb2b(): void {
  // The reb2b vendor loader (idempotent — bails early if it has
  // already run on this page). We rewrite the documented snippet in
  // TS so the linter is happy and the window mutation is typed.
  const w = window as unknown as { reb2b?: { loaded: boolean } };
  if (w.reb2b) return;
  w.reb2b = { loaded: true };
  appendScript(
    `https://ddwl4m2hdecbv.cloudfront.net/b/${REB2B_KEY}/`
    + `${REB2B_KEY}.js.gz`,
  );
}


type LintrkFn = ((event: string, payload?: unknown) => void) & {
  q?: unknown[][];
};


function injectLinkedIn(): void {
  // LinkedIn Insight Tag. The documented snippet always inserts a
  // new <script> tag even if ``lintrk`` is already defined — that
  // double-loads insight.min.js if our useEffect re-runs. Guard with
  // our own marker so the script lands exactly once per page load.
  const w = window as unknown as {
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: LintrkFn;
    __benecrmLinkedInLoaded?: boolean;
  };
  if (w.__benecrmLinkedInLoaded) return;
  w.__benecrmLinkedInLoaded = true;

  w._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  w._linkedin_data_partner_ids = w._linkedin_data_partner_ids ?? [];
  w._linkedin_data_partner_ids.push(LINKEDIN_PARTNER_ID);

  if (!w.lintrk) {
    const queue: unknown[][] = [];
    const fn = ((event: string, payload?: unknown) => {
      queue.push([event, payload]);
    }) as LintrkFn;
    fn.q = queue;
    w.lintrk = fn;
  }

  appendScript("https://snap.licdn.com/li.lms-analytics/insight.min.js");
}


export function MarketingScripts(): null {
  const { pathname } = useLocation();
  const { status } = useConsent();
  const is_public = isPublicPath(pathname);
  const has_consent = status === "accepted";
  useEffect(() => {
    if (!is_public || !has_consent) return;
    injectReb2b();
    injectLinkedIn();
  }, [is_public, has_consent]);
  return null;
}
